<template>
    <div class="cronograma-container">
        <div class="d-flex align-items-center mb-3">
            <span class="mr-2 font-weight-bold">Status do Cronograma:</span>
            <b-badge :variant="statusLabel.variant">
                <span class="h6 text-uppercase ml-1 mr-1">{{ statusLabel.texto }}</span>
            </b-badge>
        </div>

        <div v-if="foiDevolvido" class="alert alert-warning border-left-warning shadow-sm">
            <i class="fa fa-exclamation-circle mr-2"></i>
            <strong>Cronograma devolvido para correção.</strong>
            <div class="mt-1"><strong>Motivo:</strong> {{ motivoDevolucao }}</div>
        </div>

        <div class="table-responsive table-wrapper mb-4">
            <table class="table table-bordered custom-table mb-0">
                <thead class="thead-custom">
                    <tr>
                        <th class="sticky-col col-item text-center">Item</th>
                        <th class="sticky-col col-desc">Descrição</th>
                        <th class="sticky-col col-contrato text-center">Contrato</th>
                        <th class="sticky-col col-situacao text-center border-right-0">Situação</th>
                        <th class="sticky-col col-tipo text-center border-left-0"></th>
                        <th class="sticky-col col-acumulado text-center">Acumulado</th>

                        <th v-for="i in quantidadeBms" :key="i" class="text-center min-w-bm">
                            BM {{ (boletimInicial + i - 1) | formatarNumero }}
                        </th>
                    </tr>
                </thead>

                <tbody v-if="carregando">
                    <tr>
                        <td :colspan="6 + quantidadeBms" class="text-center p-3">
                            <i class="fa fa-spinner fa-spin"></i> Carregando itens...
                        </td>
                    </tr>
                </tbody>

                <tbody v-else v-for="item in itensCronograma" :key="item.id">
                    <tr>
                        <td rowspan="2" class="align-middle text-center sticky-col col-item bg-white font-weight-bold">
                            {{ item.item }}
                        </td>
                        <td rowspan="2" class="align-middle sticky-col col-desc bg-white">
                            <div class="truncate-text" v-b-tooltip.hover :title="item.descricao">
                                {{ item.descricao }}
                            </div>
                        </td>
                        <td rowspan="2" class="align-middle text-center sticky-col col-contrato bg-white">
                            {{ item.total | currency }}
                        </td>

                        <td rowspan="2" class="align-middle sticky-col col-situacao bg-gray-standard">Planejado</td>

                        <td class="sticky-col col-tipo bg-gray-standard">Percentual</td>

                        <td class="sticky-col col-acumulado bg-white text-right font-weight-bold">
                            {{ item.acumuladoPercentual | percentual }}
                        </td>

                        <td v-for="(bm, bmIndex) in item.bms" :key="`pct-${bmIndex}`" class="p-1">
                             <div class="input-group input-group-sm">
                                 <input
                                    type="tel"
                                    class="form-control text-right input-tabela"
                                    :value="formatarValorVisual(bm.percentual)"
                                    :disabled="!podeEditar || modoAnalise"
                                    @input="aoDigitarPercentual($event, bm, item)"
                                 >
                                 <div class="input-group-append">
                                    <span class="input-group-text px-1 font-weight-bold text-muted" style="font-size: 0.7rem;">%</span>
                                 </div>
                             </div>
                        </td>
                    </tr>

                    <tr>
                        <td class="sticky-col col-tipo bg-gray-standard">Parcial</td>
                        <td class="sticky-col col-acumulado bg-white text-right font-weight-bold" :class="{'text-danger': item.acumuladoValor > item.valorTotalItem + 0.01}">
                            <span v-if="item.acumuladoValor > item.valorTotalItem + 0.01" v-b-tooltip.hover title="Valor acumulado excede o total do item">
                                <i class="fa fa-exclamation-circle text-danger mr-1"></i>
                            </span>
                            {{ item.acumuladoValor | currency }}
                        </td>

                        <td v-for="(bm, bmIndex) in item.bms" :key="`val-${bmIndex}`" class="text-right">
                             <span class="text-muted">{{ bm.valor | currency }}</span>
                        </td>
                    </tr>
                </tbody>

                <tbody v-if="!carregando && itensCronograma.length > 0">
                    <tr class="bg-gray-standard font-weight-bold">
                        <td colspan="3" rowspan="2" class="align-middle pl-4 sticky-col col-total-geral">
                            Total Geral
                        </td>
                        <td rowspan="2" class="align-middle sticky-col col-situacao bg-gray-standard">Planejado</td>

                        <td class="sticky-col col-tipo bg-gray-standard">Percentual</td>
                        <td class="sticky-col col-acumulado bg-gray-standard text-right">
                            {{ totalPlanejadoPercentual | percentual }}
                        </td>

                        <td v-for="(totalBm, i) in totaisBms" :key="`tot-pct-${i}`" class="bg-gray-standard text-right">
                            {{ calcularPercentualGeral(totalBm) | percentual }}
                        </td>
                    </tr>
                    <tr class="bg-gray-standard font-weight-bold">
                        <td class="sticky-col col-tipo bg-gray-standard">Parcial</td>
                        <td class="sticky-col col-acumulado bg-gray-standard text-right">
                            {{ totalPlanejadoValor | currency }}
                        </td>

                         <td v-for="(totalBm, i) in totaisBms" :key="`tot-val-${i}`" class="bg-gray-standard text-right">
                            {{ totalBm | currency }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <b-row class="mb-4">
            <b-col md="4">
                <div class="card-resumo card-saldo">
                    <span class="titulo-resumo">Saldo Total do Contrato</span>
                    <div class="mt-2">
                        <h3 class="font-weight-bold mb-0">100,00%</h3>
                        <span class="valor-monetario">{{ valorTotalContrato | currency }}</span>
                    </div>
                </div>
            </b-col>
            <b-col md="4">
                <div class="card-resumo card-planejado">
                    <span class="titulo-resumo">Total Planejado</span>
                    <div class="mt-2">
                        <h3 class="font-weight-bold mb-0" :class="{'text-success': Math.abs(totalPlanejadoPercentual - 100) < 0.01, 'text-danger': totalPlanejadoPercentual > 100.01}">
                            {{ totalPlanejadoPercentual | percentual }}
                        </h3>
                        <span class="valor-monetario">{{ totalPlanejadoValor | currency }}</span>
                    </div>
                </div>
            </b-col>
            <b-col md="4">
                <div class="card-resumo card-diferenca" :class="{ 'bg-danger-light': diferencaValor < -0.01 }">
                    <span class="titulo-resumo">Diferença (A Planejar)</span>
                    <div class="mt-2">
                        <h3 class="font-weight-bold mb-0" :class="{ 'text-danger': Math.abs(diferencaPercentual) > 0.01 }">
                            {{ diferencaPercentual | percentual }}
                        </h3>
                        <span class="valor-monetario" :class="{ 'text-danger': diferencaValor < -0.01 }">
                            {{ diferencaValor | currency }}
                        </span>
                    </div>
                </div>
            </b-col>
        </b-row>

        <div v-if="itensInvalidos" class="alert alert-danger mb-3 shadow-sm border-left-danger">
            <i class="fa fa-exclamation-triangle mr-2"></i>
            <strong>Atenção!</strong> Existem itens com valor planejado excedendo o valor total do item.
        </div>

        <div v-if="!modoAnalise" class="d-flex justify-content-start mb-3">
            <b-button variant="warning" class="mr-2" @click="$emit('cancelar')" :disabled="salvando">
                <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.75 8.75L0.75 4.75M0.75 4.75L4.75 0.75M0.75 4.75H8.75C9.81087 4.75 10.8283 5.17143 11.5784 5.92157C12.3286 6.67172 12.75 7.68913 12.75 8.75C12.75 9.81087 12.3286 10.8283 11.5784 11.5784C10.8283 12.3286 9.81087 12.75 8.75 12.75H6.75" stroke="#2D3439" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Voltar
            </b-button>
            <b-button variant="primary" @click="salvarCronograma" :disabled="!podeSalvar">
                <i class="fa" :class="salvando ? 'fa-spinner fa-spin' : 'fa-save'"></i> Salvar
            </b-button>
            <b-button
                variant="success"
                class="ml-2"
                v-if="podeEditar"
                :disabled="!podeEnviar"
                @click="abrirModalEnvio">
                <i class="fa fa-paper-plane"></i> Enviar para Aprovação
            </b-button>
        </div>

        <b-modal
            id="modal-enviar-cronograma"
            title="Enviar Cronograma para Aprovação"
            ok-title="Sim, enviar"
            cancel-title="Cancelar"
            ok-variant="success"
            @ok="confirmarEnvio">
            Ao enviar, o cronograma será encaminhado para aprovação de Suape.
            Você não poderá editá-lo até receber retorno. Deseja continuar?
        </b-modal>
    </div>
</template>

<script>
import ContratosServicos from '../ContratosServicos';
import BoletimMedicaoServicos from './BoletimMedicaoServicos';
import { statusCronogramaEnum } from './BoletimMedicao.js';

export default {
    name: 'CronogramaPlanejamento',
    props: {
        contrato: { type: Object, required: true },
        boletinsMedicoes: { type: Array, default: () => [] },
        quantidadeBms: { type: Number, default: 1 },
        dadosPlanejamento: { type: Object, default: null },
        statusCronograma: { type: String, default: 'Rascunho' },
        motivoDevolucao: { type: String, default: '' },
        modoAnalise: { type: Boolean, default: false },
        // Número do BM a partir do qual a numeração deve começar quando ainda não existe
        // nenhum BM salvo no cronograma (contratos oriundos de uma Proposta de Preço de
        // Implantação podem iniciar a numeração em um valor diferente de 1 — ver `boletimInicial`).
        offsetInicialBm: { type: Number, default: 1 }
    },
    data() {
        return {
            carregando: false,
            salvando: false,
            itensCronograma: [],
            totalPlanejadoValor: 0,
            totaisBms: []
        }
    },
    computed: {
        valorTotalContrato() {
            return this.contrato ? this._converterMoedaParaNumero(this.contrato.valorTotal) : 0;
        },
        totalPlanejadoPercentual() {
            if (this.valorTotalContrato === 0) return 0;
            return (this.totalPlanejadoValor / this.valorTotalContrato) * 100;
        },
        diferencaValor() {
            return this.valorTotalContrato - this.totalPlanejadoValor;
        },
        diferencaPercentual() {
             if (this.valorTotalContrato === 0) return 0;
             return (this.diferencaValor / this.valorTotalContrato) * 100;
        },
        itensInvalidos() {
            if (!this.itensCronograma || this.itensCronograma.length === 0) return false;
            return this.itensCronograma.some(item => item.acumuladoValor > item.valorTotalItem + 0.01);
        },
        itensIncompletos() {
            return this.itensCronograma.some(i => Math.abs((i.acumuladoPercentual || 0) - 100) > 0.01);
        },
        podeSalvar() {
            return this.podeEditar && !this.salvando && !this.carregando && this.itensCronograma.length > 0;
        },
        podeEditar() {
            return this.statusCronograma === statusCronogramaEnum.Rascunho || this.statusCronograma === statusCronogramaEnum.DevolvidoParaCorrecao;
        },
        foiDevolvido() {
            return this.statusCronograma === statusCronogramaEnum.DevolvidoParaCorrecao;
        },
        statusLabel() {
            const statusMap = {
                [statusCronogramaEnum.Rascunho]: { texto: 'Rascunho', variant: 'secondary' },
                [statusCronogramaEnum.AguardandoAprovacao]: { texto: 'Aguardando Aprovação', variant: 'warning' },
                [statusCronogramaEnum.DevolvidoParaCorrecao]: { texto: 'Devolvido para Correção', variant: 'danger' },
                [statusCronogramaEnum.Aprovado]: { texto: 'Aprovado', variant: 'success' }
            };
            return statusMap[this.statusCronograma] || statusMap[statusCronogramaEnum.Rascunho];
        },
        podeEnviar() {
            return this.podeEditar && !this.salvando && !this.carregando && this.itensCronograma.length > 0
                && !this.itensInvalidos && !this.itensIncompletos && Math.abs(this.diferencaValor) <= 0.01;
        },
        boletimInicial() {
            let min = null;
            this.itensCronograma.forEach(item => {
                (item.bms || []).forEach(bm => {
                    if (bm.boletim != null && (min === null || bm.boletim < min)) min = bm.boletim;
                });
            });
            // Se ainda não há nenhum BM salvo (cronograma novo), a numeração deve iniciar a
            // partir do offset recebido (numeroProximoBM da Proposta de Preço de Implantação),
            // em vez de sempre assumir 1.
            return min || this.offsetInicialBm || 1;
        }
    },
    methods: {
        async iniciarDados() {
            this.carregando = true;

            if (this.dadosPlanejamento) {
                const listaBruta = this._extrairDadosResponse(this.dadosPlanejamento);

                // Se dadosPlanejamento tiver itens dentro de uma propriedade 'itens'
                const itensParaMapear = this.dadosPlanejamento.itens ? this.dadosPlanejamento.itens : listaBruta;

                this.itensCronograma = itensParaMapear.map(item => this._mapearItemCronograma(item));

                // Recalcular totais gerais após carregar o planejamento
                this.itensCronograma.forEach(item => {
                   this._atualizarTotaisDoItem(item);
                });
                this._recalcularTotaisGerais();

                this.carregando = false;
                return;
            }

            if(!this.contrato || !this.contrato.contratoId) {
                this.carregando = false;
                return;
            }

            try {
                this.$store.dispatch('addRequest');
                const response = await ContratosServicos.obterItensPai(this.contrato.contratoId);
                const listaBruta = this._extrairDadosResponse(response);

                this.itensCronograma = listaBruta.map(item => this._mapearItemCronograma(item));
                this._recalcularTotaisGerais();

            } catch (error) {
                console.error("Erro ao carregar itens:", error);
                this.$toast.error("Erro ao carregar itens do contrato.");
            } finally {
                this.$store.dispatch('removeRequest');
                this.carregando = false;
            }
        },

        salvarCronograma() {
            this.salvando = true;
            this.$store.dispatch('addRequest');

            const payloadFinal = this._construirPayload();

            BoletimMedicaoServicos.salvarPlanejamentoFisicoFinanceiro(this.contrato.contratoId, payloadFinal)
                .then(response => {
                    if (response && response.success) {
                        this.$toast.success('Cronograma planejado com sucesso!');
                        this.$emit('salvo', payloadFinal);
                    } else {
                        this.$toast.error('Erro ao salvar planejamento.');
                        if(response && response.errors && response.errors.length > 0) {
                            response.errors.forEach(err => this.$toast.error(err));
                        }
                    }
                })
                .catch(error => {
                    console.error("Erro ao salvar:", error);
                    this.$toast.error('Erro ao salvar planejamento.');
                })
                .finally(() => {
                    this.salvando = false;
                    this.$store.dispatch('removeRequest');
                });
        },

        aoDigitarPercentual(event, bm, item) {
            let valorLimpo = event.target.value.replace(/\D/g, '');
            if (!valorLimpo) valorLimpo = '0';

            const numeroFinal = this._arredondar(parseInt(valorLimpo) / 100);

            bm.percentual = numeroFinal;
            event.target.value = this.formatarValorVisual(numeroFinal);

            this.recalcularItem(item);
        },

        recalcularItem(item) {
            this._atualizarValoresDosBms(item);
            this._atualizarTotaisDoItem(item);
            this._recalcularTotaisGerais();
        },

        formatarValorVisual(valor) {
            if (!valor) return '0,00';
            return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        calcularPercentualGeral(valorTotalBm) {
            if (this.valorTotalContrato === 0) return '0.00';
            return ((valorTotalBm / this.valorTotalContrato) * 100).toFixed(2);
        },

        _converterMoedaParaNumero(valor) {
            if (!valor) return 0;
            if (typeof valor === 'number') return valor;
            let limpo = valor.toString().replace(/[^\d,-]/g, '').replace(',', '.');
            return parseFloat(limpo) || 0;
        },

        _arredondar(valor) {
            if (!valor) return 0;
            let val = valor;
            if (typeof val === 'string') {
                val = parseFloat(val);
            }
            if (isNaN(val)) return 0;
            return parseFloat(val.toFixed(2));
        },

        _extrairDadosResponse(response) {
            if(response && response.success) return response.data;
            if(Array.isArray(response)) return response;
            if(response.data && Array.isArray(response.data)) return response.data;
            return [];
        },

        _mapearItemCronograma(item) {
            const valTotal = this._calcularValorTotalItem(item);

            let bms = [];
            // Verifica se o item já possui BMs (seja diretamente ou dentro de 'planejado')
            let bmsExistentes = null;
            if (item.bms && Array.isArray(item.bms)) {
                bmsExistentes = item.bms;
            } else if (item.medicoes && Array.isArray(item.medicoes)) {
                bmsExistentes = item.medicoes;
            } else if (item.planejado && item.planejado.bms && Array.isArray(item.planejado.bms)) {
                bmsExistentes = item.planejado.bms;
            }

            if (bmsExistentes) {
                bms = [...bmsExistentes]
                    .sort((a, b) => (a.boletim || a.Boletim || 0) - (b.boletim || b.Boletim || 0))
                    .map(b => ({
                        valor: b.valor || b.valorCalculado || 0,
                        percentual: b.percentual || b.percentualInformado || 0,
                        boletim: b.boletim || b.Boletim || null
                    }));
            }

            // Ajusta o tamanho do array de BMs para corresponder à quantidadeBms selecionada
            const currentLen = bms.length;
            const qtdeDesejada = this.quantidadeBms || 1;

            if (currentLen < qtdeDesejada) {
                const diff = qtdeDesejada - currentLen;
                for (let i = 0; i < diff; i++) {
                    bms.push({ valor: 0, percentual: 0 });
                }
            } else if (currentLen > qtdeDesejada) {
                // Corta se houver mais do que o solicitado
                bms = bms.slice(0, qtdeDesejada);
            }

            return {
                ...item,
                id: item.id || item.itemContratoId,
                item: item.item || item.ordem || item.seqRM || item.itemContratoId,
                itemContratoId: item.itemContratoId,
                contrato: item.contrato,
                total: this._arredondar(valTotal),
                descricao: item.descricao,
                valorTotalItem: this._arredondar(valTotal),
                acumuladoValor: 0,
                acumuladoPercentual: 0,
                bms: bms
            };
        },

        _calcularValorTotalItem(item) {
            let valTotal = this._converterMoedaParaNumero(item.total);

            if (valTotal === 0) {
                valTotal = this._converterMoedaParaNumero(item.valorTotalItem || item.ValorTotalItem);
            }

            if (valTotal > 0) {
                return valTotal;
            }

            const qtd = this._converterMoedaParaNumero(item.quantidade);
            const unitario = this._converterMoedaParaNumero(item.valorUnitario);
            const bdiParcela = this._converterMoedaParaNumero(item.valorUnitarioComBDI);
            const unitarioComBDI = unitario + bdiParcela;
            valTotal = qtd * (unitarioComBDI > 0 ? unitarioComBDI : unitario);

            if (valTotal === 0) {
                valTotal = this._converterMoedaParaNumero(item.saldoValor);
            }

            if (valTotal === 0) {
                valTotal = this._converterMoedaParaNumero(item.valorTotal);
            }

            return valTotal;
        },

        _atualizarValoresDosBms(item) {
            const valItem = item.valorTotalItem;

            item.bms.forEach(bm => {
                const pct = parseFloat(bm.percentual) || 0;
                bm.valor = valItem > 0 ? this._arredondar((pct / 100) * valItem) : 0;
            });

            const totalPercentual = item.bms.reduce((acc, bm) => acc + (parseFloat(bm.percentual) || 0), 0);
            if (Math.abs(totalPercentual - 100) < 0.01) {
                const somaCalculada = item.bms.reduce((acc, bm) => acc + bm.valor, 0);
                const diferenca = this._arredondar(valItem - somaCalculada);

                if (Math.abs(diferenca) > 0) {
                    for (let i = item.bms.length - 1; i >= 0; i--) {
                        if ((parseFloat(item.bms[i].percentual) || 0) > 0) {
                            item.bms[i].valor = this._arredondar(item.bms[i].valor + diferenca);
                            break;
                        }
                    }
                }
            }
        },

        _atualizarTotaisDoItem(item) {
            const totalPct = item.bms.reduce((acc, bm) => acc + (parseFloat(bm.percentual) || 0), 0);
            const totalVal = item.bms.reduce((acc, bm) => acc + (parseFloat(bm.valor) || 0), 0);

            this.$set(item, 'acumuladoPercentual', this._arredondar(totalPct));
            this.$set(item, 'acumuladoValor', this._arredondar(totalVal));
        },

        _recalcularTotaisGerais() {
            this.totalPlanejadoValor = this.itensCronograma.reduce((acc, item) => acc + (item.acumuladoValor || 0), 0);

            const totaisVerticais = Array(this.quantidadeBms).fill(0);

            this.itensCronograma.forEach(item => {
                item.bms.forEach((bm, index) => {
                    const val = parseFloat(bm.valor) || 0;
                    if (totaisVerticais[index] !== undefined) {
                        totaisVerticais[index] += val;
                    }
                });
            });

            this.totaisBms = totaisVerticais;
        },

        abrirModalEnvio() {
            if (this.itensInvalidos || Math.abs(this.diferencaValor) > 0.01) {
                this.$toast.warning('Revise o cronograma: o Total Planejado deve ser igual ao Saldo do Contrato e nenhum item pode exceder seu total.');
                return;
            }

            const itensIncompletos = this.itensCronograma.filter(item => {
                const pct = item.acumuladoPercentual || 0;
                return Math.abs(pct - 100) > 0.01;
            });

            if (itensIncompletos.length > 0) {
                const ids = itensIncompletos.map(i => i.itemContratoId).join(', ');
                this.$toast.warning(`Os itens a seguir não estão 100% planejados: ${ids}. O acumulado deve ser 100%.`, 'Atenção');
                return;
            }

            this.$bvModal.show('modal-enviar-cronograma');
        },

        confirmarEnvio() {
            this.salvando = true;
            this.$store.dispatch('addRequest');
            const payload = this._construirPayload();
            BoletimMedicaoServicos.salvarPlanejamentoFisicoFinanceiro(this.contrato.contratoId, payload)
                .then(saveResponse => {
                    if (!saveResponse || !saveResponse.success) {
                        throw new Error('Falha ao salvar o cronograma.');
                    }
                    return BoletimMedicaoServicos.enviarCronogramaParaAprovacao(this.contrato.contratoId);
                })
                .then(response => {
                    if (response && response.success) {
                        this.$toast.success('Cronograma enviado para aprovação da Suape.');
                        this.$emit('enviado');
                    } else {
                        this.$toast.error('Erro ao enviar cronograma.');
                        if (response && response.errors) response.errors.forEach(e => this.$toast.error(e));
                    }
                })
                .catch(() => this.$toast.error('Erro ao enviar cronograma.'))
                .finally(() => { this.salvando = false; this.$store.dispatch('removeRequest'); });
        },

        _construirPayload() {
            const itensPayload = this.itensCronograma.map(item => ({
                itemContratoId: item.itemContratoId,
                descricao: item.descricao,
                ValorTotalItem: item.valorTotalItem,
                valorAcumulado: this._arredondar(item.acumuladoValor),
                percentualAcumulado: this._arredondar(item.acumuladoPercentual),
                cronograma: item.bms.map((bm, idx) => ({
                    boletim: this.boletimInicial + idx,
                    percentualInformado: this._arredondar(bm.percentual),
                    valorCalculado: this._arredondar(bm.valor)
                }))
            }));

            return {
                contratoId: this.contrato.contratoId || this.contrato.id,
                gestor: this.contrato.gestor,
                vigencia: this.contrato.vigencia || this.contrato.dataVigencia,
                valorTotalContrato: this.valorTotalContrato,
                itens: itensPayload
            };
        }
    },
    created() {
        this.iniciarDados();
    },
    watch: {
        quantidadeBms() {
            if(this.itensCronograma.length > 0) {
                 this.itensCronograma.forEach(item => {
                     // Aqui precisamos ter cuidado para não perder dados ao redimensionar
                     // mas como o usuario pediu "alterar qtde", assumimos reset ou ajuste.
                     // Na logica anterior eu estava resetando.
                     // Vou manter reset ou ajuste?
                     // Se eu apenas mudar a visualização, devo usar slice/pad

                     let currentBms = item.bms;
                     if(currentBms.length < this.quantidadeBms) {
                         const diff = this.quantidadeBms - currentBms.length;
                         for (let i = 0; i < diff; i++) {
                             currentBms.push({ valor: 0, percentual: 0 });
                         }
                     } else if (currentBms.length > this.quantidadeBms) {
                         currentBms = currentBms.slice(0, this.quantidadeBms);
                     }

                     item.bms = currentBms;
                     this._atualizarTotaisDoItem(item);
                 });
                 this._recalcularTotaisGerais();
            } else {
                this.iniciarDados();
            }
        },
        'contrato.contratoId': function() {
            this.iniciarDados();
        },
        // Adicionar watch para dadosPlanejamento caso mude dinamicamente
        dadosPlanejamento: function(newVal) {
             if (newVal) {
                 this.iniciarDados();
             }
        }
    },
    filters: {
        currency(value) {
            if (value === null || value === undefined) return 'R$ 0,00';
            return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        },
        formatarNumero(value) {
            if(!value) return '01';
            return String(value).padStart(2, '0');
        }
    }
}
</script>

<style scoped>
.cronograma-container { width: 100%; position: relative; }
.table-wrapper { overflow-x: auto; width: 100%; box-shadow: 0 0 5px rgba(0,0,0,0.1); }
.custom-table { font-size: 0.75rem; width: max-content; min-width: 100%; border-collapse: separate; border-spacing: 0; }
.custom-table th, .custom-table td { vertical-align: middle; padding: 0.35rem; white-space: nowrap; border: 1px solid #ced4da; background-clip: padding-box; }
.bg-gray-standard, .thead-custom th { background-color: #e2e3e5 !important; color: #25282A; }
.bg-white { background-color: #fff !important; }
.min-w-bm { min-width: 130px; }
.truncate-text { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sticky-col { position: sticky; z-index: 2; }
.thead-custom th.sticky-col { z-index: 3; }
.col-item { left: 0; width: 35px; border-right: 1px solid #ced4da; }
.col-desc { left: 35px; width: 170px; border-right: 1px solid #ced4da; }
.col-contrato { left: 205px; width: 75px; border-right: 1px solid #ced4da; }
.col-situacao { left: 280px; width: 75px; border-right: 1px solid #ced4da; }
.col-tipo { left: 355px; width: 70px; border-right: 1px solid #ced4da; }
.col-acumulado { left: 425px; width: 95px; border-right: 2px solid #aaa !important; z-index: 2; box-shadow: 2px 0 5px rgba(0,0,0,0.1); }
.thead-custom th.col-acumulado { z-index: 3; background-color: #e2e3e5 !important; }
.col-total-geral { left: 0; width: 280px; z-index: 3; background-color: #e2e3e5 !important; border-right: 1px solid #ced4da; }
.input-tabela { border: 1px solid #ced4da; background-color: #fff; font-size: 0.75rem; padding: 0.2rem; }
.input-tabela:focus { box-shadow: none; border-color: #80bdff; }
.card-resumo { padding: 12px; border-radius: 8px; height: 100%; border: 1px solid rgba(0,0,0,0.05); }
.card-saldo { background-color: #e3f2fd; color: #0d47a1; }
.card-planejado { background-color: #e8f5e9; color: #1b5e20; }
.card-diferenca { background-color: #fff8e1; color: #f57f17; }
.titulo-resumo { font-size: 0.8rem; font-weight: 600; text-transform: uppercase; opacity: 0.8; }
.valor-monetario { font-size: 1rem; display: block; margin-top: 5px; }
</style>
